/**
 * ConfirmDialog.js — Password-protected AI impact confirmation dialogs
 *
 * const { confirm } = useConfirm();
 * const ok = await confirm({
 *   title: 'Delete Exam?',
 *   message: 'This will permanently remove the exam.',
 *   impact: ['45 students lose access', '120 answers deleted'],
 *   confirmLabel: 'Delete Exam',
 *   variant: 'danger',        // 'danger' | 'warning' | 'info'
 *   requirePassword: true,    // user must enter their login password
 * });
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const ConfirmContext = createContext(null);

const VARIANTS = {
  danger: {
    icon:'⛔', headerBg:'rgba(239,68,68,0.08)', headerBorder:'rgba(239,68,68,0.2)',
    titleColor:'var(--accent-danger)', btnClass:'btn-danger',
    impactLabel:'⚠ What will change', impactBg:'rgba(239,68,68,0.06)', impactBorder:'rgba(239,68,68,0.15)', impactColor:'var(--accent-danger)',
  },
  warning: {
    icon:'⚡', headerBg:'rgba(245,158,11,0.08)', headerBorder:'rgba(245,158,11,0.2)',
    titleColor:'var(--accent-warning)', btnClass:'btn-warning',
    impactLabel:'📋 What will happen', impactBg:'rgba(245,158,11,0.06)', impactBorder:'rgba(245,158,11,0.15)', impactColor:'var(--accent-warning)',
  },
  info: {
    icon:'✅', headerBg:'rgba(79,142,247,0.08)', headerBorder:'rgba(79,142,247,0.2)',
    titleColor:'var(--accent-primary)', btnClass:'btn-primary',
    impactLabel:'📌 What will happen', impactBg:'rgba(79,142,247,0.06)', impactBorder:'rgba(79,142,247,0.15)', impactColor:'var(--accent-primary)',
  },
};

function ConfirmModal({ dialog, onClose }) {
  const { title, message, impact = [], confirmLabel = 'Confirm', variant = 'danger', requirePassword } = dialog;
  const v = VARIANTS[variant] || VARIANTS.danger;

  const [pwd,         setPwd]         = useState('');
  const [pwdError,    setPwdError]    = useState('');
  const [verifying,   setVerifying]   = useState(false);
  const [pwdVerified, setPwdVerified] = useState(!requirePassword);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(false); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const verifyPassword = async () => {
    if (!pwd.trim()) { setPwdError('Please enter your password'); return; }
    setVerifying(true); setPwdError('');
    try {
      await axios.post('/api/auth/verify-password', { password: pwd });
      setPwdVerified(true);
    } catch (err) {
      setPwdError(err.response?.data?.error || 'Incorrect password. Try again.');
    } finally { setVerifying(false); }
  };

  const handleConfirm = async () => {
    if (requirePassword && !pwdVerified) { await verifyPassword(); return; }
    if (pwdVerified) onClose(true);
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose(false)} style={{
      position:'fixed', inset:0, zIndex:10000,
      background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
    }}>
      <div style={{
        background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14,
        width:'100%', maxWidth:460, boxShadow:'0 20px 60px rgba(0,0,0,0.5)',
        overflow:'hidden', animation:'cdSlide 0.18s ease',
      }}>
        {/* Header */}
        <div style={{ padding:'16px 22px', background:v.headerBg, borderBottom:`1px solid ${v.headerBorder}`, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:26 }}>{v.icon}</span>
          <div style={{ fontWeight:800, fontSize:17, color:v.titleColor }}>{title}</div>
        </div>

        {/* Body */}
        <div style={{ padding:'20px 22px' }}>
          {message && <p style={{ fontSize:14, color:'var(--text-secondary)', marginBottom:16, lineHeight:1.7 }}>{message}</p>}

          {impact.length > 0 && (
            <div style={{ background:v.impactBg, border:`1px solid ${v.impactBorder}`, borderRadius:8, padding:'12px 16px', marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:v.impactColor, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:10 }}>{v.impactLabel}</div>
              {impact.map((item, i) => (
                <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom: i < impact.length-1 ? 7 : 0 }}>
                  <span style={{ color:v.impactColor, flexShrink:0, marginTop:2, fontSize:13 }}>•</span>
                  <span style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          )}

          {/* Password gate */}
          {requirePassword && !pwdVerified && (
            <div style={{ marginBottom:16, padding:'14px 16px', background:'rgba(124,58,237,0.07)', border:'1px solid rgba(124,58,237,0.2)', borderRadius:10 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--accent-secondary)', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
                🔐 Password Required to Continue
              </div>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:10 }}>
                Enter your ExamAI login password to authorize this operation.
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input ref={inputRef} type="password" className="form-input" placeholder="Your login password"
                  value={pwd} onChange={e => { setPwd(e.target.value); setPwdError(''); }}
                  onKeyDown={e => e.key === 'Enter' && verifyPassword()}
                  style={{ flex:1, padding:'8px 12px', fontSize:14 }} />
                <button className="btn btn-secondary btn-sm" onClick={verifyPassword} disabled={verifying}>
                  {verifying ? '⏳' : '✓ Verify'}
                </button>
              </div>
              {pwdError && <div style={{ color:'var(--accent-danger)', fontSize:12, marginTop:8 }}>⚠ {pwdError}</div>}
            </div>
          )}
          {requirePassword && pwdVerified && (
            <div style={{ marginBottom:16, padding:'10px 14px', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.3)', borderRadius:8, fontSize:13, color:'var(--accent-success)', fontWeight:600 }}>
              ✓ Password verified — you may proceed
            </div>
          )}

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => onClose(false)}>Cancel</button>
            <button className={`btn ${v.btnClass}`} onClick={handleConfirm} disabled={verifying}>
              {verifying ? '⏳ Verifying...' : requirePassword && !pwdVerified ? '🔐 Verify Password First' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes cdSlide{from{opacity:0;transform:translateY(10px) scale(.97)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const confirm = useCallback((opts) => new Promise(resolve => setDialog({ ...opts, resolve })), []);
  const close = (result) => { if (dialog) dialog.resolve(result); setDialog(null); };
  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {dialog && <ConfirmModal dialog={dialog} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() { return useContext(ConfirmContext); }
